import { useEffect, useState } from 'react'
import { ActivityTags } from '../activities/ActivityTags'
import { DateComponent } from '../DateComponent'
import NameIcon from '../shared/NameIcon'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { ActivityJson } from 'app/types/entities'
import type { CallJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { FormJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import type { NotificationType, NotificationTypes } from 'app/types/entities'
import type { SchedulerJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { sortTags } from 'lib/sortTags'
import { gqlFetchOne } from 'app/lib/graphqlClient'

function getNotificationTitle(objectType: NotificationType[`objectType`], types: NotificationTypes[]) {
  switch (objectType) {
    case `call`: {
      if (types?.includes(`ai-emergency-appointment-request`)) return `Emergency Request`
      if (types?.includes(`missed-call`)) return `Missed Call`
      if (types?.includes(`voicemail`)) return `Voicemail`
      return `New Call`
    }
    case `message`: {
      return `New SMS`
    }
    case `form`: {
      return `Form Submission`
    }
    case `contact`: {
      if (types?.includes(`no-patient-linked`)) return `No Patient Linked`
      if (types?.includes(`no-appointment-found`)) return `No Appointment Found`
      if (types?.includes(`broken-appointment`)) return `Broken Appointment`
      return `New Contact`
    }
    case `scheduler`: {
      return `New Appointment Booked`
    }
    default: {
      return `New Notification`
    }
  }
}

function getNotificationConnectorText(objectType: NotificationType[`objectType`], types: NotificationTypes[]) {
  switch (objectType) {
    case `call`: {
      return `from`
    }
    case `message`: {
      return `from`
    }
    case `form`: {
      return `from`
    }
    case `contact`: {
      if (types?.includes(`broken-appointment`)) return `by`
      return `on`
    }
    case `scheduler`: {
      return `by`
    }
    default: {
      return `from`
    }
  }
}

function renderNotificationSubject(
  objectType: NotificationType[`objectType`],
  data: { activity?: ActivityJson; message?: MessageJson; contact?: ContactJson },
) {
  const ContactDiv = ({ initials, name }) => {
    return (
      <div className="flex items-center gap-x-1">
        <NameIcon fullName={initials} heightClassName="h-5" widthClassName="w-5" isTiny />
        <p className="text-strong max-w-56 overflow-hidden text-ellipsis whitespace-nowrap text-label-sm">{name}</p>
      </div>
    )
  }
  if (objectType === `call`) {
    if (data.activity?.nameFromActivity) {
      const initials = data.activity.nameFromActivity.split(` `).at(0)?.at(0)?.toLocaleUpperCase()
      return (
        <div className="flex items-center gap-x-1">
          <ContactDiv initials={initials} name={data.activity.nameFromActivity} />
          {data.activity.contacts && data.activity.contacts?.length > 1 ? (
            <div className="tag px-1.5">{`+${data.activity.contacts.length - 1}`}</div>
          ) : null}
        </div>
      )
    }
    return <p className="text-strong text-label-sm">{formatPhoneNumber(data.activity?.phoneFromActivity)}</p>
  }
  if (objectType === `message`) {
    if (data.message?.contactId && data.contact && data.contact.name) {
      const initials = data.contact.name.split(` `).at(0)?.at(0)?.toLocaleUpperCase()
      return <ContactDiv initials={initials} name={data.contact.name} />
    }
    return <p className="text-strong text-label-sm">{formatPhoneNumber(data.message?.from)}</p>
  }
  if (objectType === `form`) {
    const initials = data?.activity?.nameFromActivity?.split(` `).at(0)?.at(0)?.toLocaleUpperCase()
    return <ContactDiv initials={initials} name={data.activity?.nameFromActivity} />
  }
  if (objectType === `contact`) {
    const initials = (data?.contact?.name ?? ``).split(` `).at(0)?.at(0)?.toLocaleUpperCase()
    return <ContactDiv initials={initials} name={data.contact?.name ?? ``} />
  }
  if (objectType === `scheduler`) {
    const initials = data?.activity?.nameFromActivity?.split(` `).at(0)?.at(0)?.toLocaleUpperCase()
    return <ContactDiv initials={initials} name={data.activity?.nameFromActivity} />
  }
}

export default function LazyNotificationCard({
  notification,
  onForceClosePanel,
  onMarkAsRead,
}: {
  notification: NotificationType
  onForceClosePanel: () => void
  onMarkAsRead: (read: boolean, notificationId: string) => Promise<void>
}) {
  const [loading, setLoading] = useState(false)
  const [activity, setActivity] = useState<ActivityJson | undefined>(undefined)
  const [call, setCall] = useState<CallJson | undefined>(undefined)
  const [form, setForm] = useState<FormJson | undefined>(undefined)
  const [scheduler, setScheduler] = useState<SchedulerJson | undefined>(undefined)
  const [message, setMessage] = useState<MessageJson | undefined>(undefined)
  const [contact, setContact] = useState<ContactJson | undefined>(undefined)

  const handleNotificationClick = async () => {
    await onMarkAsRead(true, notification.id!)
    const wId = activity?.workspaceId || contact?.workspaceId || ``
    if (notification.objectType === `call` && activity?.contacts && activity.contacts.length > 1) {
      window.location.href = `/activities?drawer=${notification.activityId}&wId=${wId}`
    } else if (
      notification.objectType === `call` ||
      notification.objectType === `form` ||
      notification.objectType === `scheduler`
    ) {
      window.location.href = `/contacts?drawer=${activity?.contacts?.at(0)?.id}&wId=${wId}`
    } else if (notification.objectType === `message`) {
      window.location.href = `/inbox?locationId=${message?.locationId}&tab=${message?.threadId}`
    } else if (notification.objectType === `contact`) {
      window.location.href = `/contacts?drawer=${notification.contactId}&wId=${wId}`
    }
    onForceClosePanel()
  }

  useEffect(() => {
    bg(async () => {
      setLoading(true)
      try {
        if (
          notification.objectType === `call` ||
          notification.objectType === `form` ||
          notification.objectType === `scheduler`
        ) {
          if (!notification.activityId) return
          const activityData = await gqlFetchOne<ActivityJson>('activity', notification.activityId)
          if (!activityData) return
          setActivity(activityData)
          if (!activityData.typeId) return
          if (notification.objectType === `call`) {
            setCall(await gqlFetchOne<CallJson>('call', activityData.typeId))
          } else if (notification.objectType === `scheduler`) {
            setScheduler(await gqlFetchOne<SchedulerJson>('scheduler', activityData.typeId))
          } else {
            // Form records have no GraphQL surface yet, so this stays on the same REST
            // route FormCard uses.
            const response = await window
              .fetch(`/api/activities/${notification.activityId}/Form/${activityData.typeId}`, {
                credentials: `include`,
              })
              .then((r) => r.json())
            setForm(response?.[`record`] as FormJson)
          }
        }
        if (notification.objectType === `message`) {
          if (!notification.messageId) return
          const messageData = await gqlFetchOne<MessageJson>('message', notification.messageId)
          if (!messageData) return
          setMessage(messageData)
          if (messageData.contactId) {
            const contactData = await gqlFetchOne<ContactJson>('contact', messageData.contactId)
            if (contactData) setContact(contactData)
          }
        }
        if (notification.objectType === `contact`) {
          if (!notification.contactId) return
          const contactData = await gqlFetchOne<ContactJson>('contact', notification.contactId)
          if (contactData) setContact(contactData)
        }
      } finally {
        setLoading(false)
      }
    })
  }, [notification.objectType, notification.activityId, notification.messageId, notification.contactId])

  return (
    <>
      {!loading ? (
        <Template
          isRead={notification.read}
          onMarkAsRead={(read) => onMarkAsRead(read, notification.id!)}
          objectType={notification.objectType}
          types={notification.types!}
          activity={activity}
          call={call}
          form={form}
          scheduler={scheduler}
          message={message}
          contact={contact}
          triggerDate={notification.triggerDate!}
          onNotificationClick={handleNotificationClick}
        />
      ) : (
        <div className="w-full animate-pulse animate-normal animate-fill-forwards animate-infinite animate-ease-in-out">
          <div className="flex w-full flex-col gap-y-3">
            <div className="flex items-center">
              <div className="h-3 w-3 rounded-full bg-surface-strong"></div>
              <div className="ml-3 flex w-full items-center justify-between">
                <div className="flex h-5 w-72 items-center gap-x-[5px] rounded bg-surface-strong">
                  <p className="text-strong text-label-sm"></p>
                </div>
                <p className="text-subtle h-5 w-24 rounded bg-surface-strong text-body-xs leading-none"></p>
              </div>
            </div>
            <div className="ml-6 rounded-lg bg-surface-strong p-3.5">
              <p className="text-strong line-clamp-4 h-[100px] text-body-xs"></p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Template({
  isRead,
  onMarkAsRead,
  objectType,
  types,
  activity,
  call,
  form,
  scheduler,
  message,
  contact,
  triggerDate,
  onNotificationClick,
}: {
  isRead?: boolean
  onMarkAsRead: (read: boolean) => Promise<void>
  objectType: NotificationType[`objectType`]
  types: NotificationTypes[]
  activity?: ActivityJson
  call?: CallJson
  form?: FormJson
  scheduler?: SchedulerJson
  message?: MessageJson
  contact?: ContactJson
  triggerDate: string
  onNotificationClick: () => Promise<void>
}) {
  const handleMarkAsRead = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>, read: boolean) => {
    e.stopPropagation()
    await onMarkAsRead(read)
  }

  return (
    <div className="flex w-full cursor-pointer flex-col gap-y-3" onClick={() => onNotificationClick()}>
      <div className="flex items-center">
        {isRead ? (
          <div
            className="h-3 w-3 cursor-pointer rounded-full border border-gray-subtle hover:bg-blue-300"
            onClick={(e) => handleMarkAsRead(e, false)}
          ></div>
        ) : (
          <div
            className="h-3 w-3 cursor-pointer rounded-full bg-blue-500 hover:bg-surface-muted"
            onClick={(e) => handleMarkAsRead(e, true)}
          ></div>
        )}
        <div className="ml-3 flex w-full items-center justify-between">
          <div className="flex items-center gap-x-[5px]">
            <p className="text-strong text-label-sm">
              {getNotificationTitle(objectType, types)}
              {` `}
              <span className="text-subtle text-body-sm">{getNotificationConnectorText(objectType, types)}</span>
            </p>
            {renderNotificationSubject(objectType, { activity, message, contact })}
          </div>
          <DateComponent value={triggerDate} className="text-caption-md text-minimal" />
        </div>
      </div>
      {objectType === `call` && !types.includes(`missed-call`) ? (
        <div className="ml-6 flex flex-wrap gap-2">
          <ActivityTags activityTags={sortTags(activity?.tags)} workspaceId={activity?.workspaceId} isLoading={false} />
        </div>
      ) : null}
      {objectType === `call` && !types.includes(`missed-call`) ? (
        <div className="ml-6 rounded-lg bg-surface-muted p-3.5">
          <p className="text-strong line-clamp-4 text-body-xs">{call?.ai?.summary}</p>
        </div>
      ) : null}
      {objectType === `message` ? (
        <div className="ml-6 rounded-lg bg-surface-muted p-3.5">
          <p className="text-strong line-clamp-4 text-body-xs">{message?.text ?? ``}</p>
        </div>
      ) : null}
      {objectType === `form` ? (
        <div className="ml-6 grid grid-cols-2 gap-y-3 rounded-lg bg-surface-muted px-4 py-3">
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Name</p>
            <p className="text-strong mt-1 text-ui-xs">{form?.patientName}</p>
          </div>
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Email</p>
            <p className="text-strong mt-1 text-ui-xs">{form?.email}</p>
          </div>
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Phone Number</p>
            <p className="text-strong mt-1 text-ui-xs">{formatPhoneNumber(form?.phone)}</p>
          </div>
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Form Title</p>
            <p className="text-strong mt-1 text-ui-xs">{form?.title}</p>
          </div>
        </div>
      ) : null}
      {objectType === `scheduler` ? (
        <div className="ml-6 grid grid-cols-2 gap-y-3 rounded-lg bg-surface-muted px-4 py-3">
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Name</p>
            <p className="text-strong mt-1 text-ui-xs">{scheduler?.name}</p>
          </div>
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Phone Number</p>
            <p className="text-strong mt-1 text-ui-xs">{formatPhoneNumber(scheduler?.phone)}</p>
          </div>
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Appointment Date</p>
            <p className="text-strong mt-1 text-ui-xs">{`${formatDate({ value: scheduler?.day, dateOnly: true, format: `MMM DD` }) || `-`}, ${
              scheduler?.time
            }`}</p>
          </div>
          <div className="col-span-1">
            <p className="text-subtle text-body-xs">Appointment Type</p>
            <p className="text-strong mt-1 text-ui-xs">{scheduler?.appointmentType}</p>
          </div>
        </div>
      ) : null}
    </div>
  )
}
